import React, { FC } from "react";
import {
  LockClosedIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/solid";
import { useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";

interface QuickSelectButtonProps {
  label: string;
  onClick: () => void;
  isReset?: boolean;
  disabled?: boolean;
}

export const QuickSelectButton: FC<QuickSelectButtonProps> = ({
  label,
  onClick,
  isReset = false,
  disabled = false
}) => {
  const navigate = useNavigate();

  const handleLockClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate("/billing");
  };

  // For disabled items with lock, we need a different structure
  if (disabled && !isReset) {
    return (
      <div className="text-left text-sm font-medium px-2 py-1 flex items-center justify-between w-full text-gray-400">
        <span>{label}</span>
        <button
          type="button"
          data-tooltip-id={`lock-tooltip-${label.replace(/\s+/g, "-")}`}
          className="bg-gray-100 rounded p-1 ml-2 cursor-pointer hover:bg-gray-200 transition-colors"
          onClick={handleLockClick}
        >
          <LockClosedIcon className="w-3 h-3 text-gray-500" />
        </button>
        <ReactTooltip
          id={`lock-tooltip-${label.replace(/\s+/g, "-")}`}
          className="!bg-brandDarkest !dark:bg-primary-800 !rounded-none max-w-sm !z-[9999]"
          place="left"
        >
          <p className="text-sm">Click to upgrade to view dates.</p>
        </ReactTooltip>
      </div>
    );
  }

  // Regular button for enabled items
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-left text-sm font-medium px-2 py-1 flex items-center justify-between w-full ${
        isReset
          ? "text-red-500 hover:text-red-600 hover:underline"
          : "text-gray-700 hover:text-rebrandBlue-600 hover:underline"
      } ${isReset ? "mt-4" : ""}`}
    >
      <span>{label}</span>
    </button>
  );
};

interface CalendarHeaderProps {
  monthDate: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  decreaseYear: () => void;
  increaseYear: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
}

export const CalendarHeader: FC<CalendarHeaderProps> = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
  decreaseYear,
  increaseYear,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => (
  <div className="flex items-center justify-between px-2 py-1 border-b">
    <div className="flex items-center space-x-1">
      <button
        onClick={decreaseYear}
        type="button"
        className="p-1 hover:bg-gray-100 rounded"
      >
        <ChevronDoubleLeftIcon className="w-4 h-4 text-gray-600" />
      </button>
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        type="button"
        className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
      >
        <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
      </button>
    </div>
    <span className="text-sm font-medium">
      {monthDate.toLocaleString("default", { month: "long" })}{" "}
      {monthDate.getFullYear()}
    </span>
    <div className="flex items-center space-x-1">
      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        type="button"
        className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
      >
        <ChevronRightIcon className="w-4 h-4 text-gray-600" />
      </button>
      <button
        onClick={increaseYear}
        type="button"
        className="p-1 hover:bg-gray-100 rounded"
      >
        <ChevronDoubleRightIcon className="w-4 h-4 text-gray-600" />
      </button>
    </div>
  </div>
);
